/**
 * Abonnement Prof (migration 0691) : 29,90 €/mois, essai de 7 jours, admins
 * exemptés.
 *
 * Un seul panneau pour deux usages :
 *  - `blocked` : l'essai (ou l'abonnement) est terminé — il REMPLACE tout
 *    l'espace prof (garde de `TutorLayout`), avec un accès au profil
 *    (déconnexion, suppression du compte) ;
 *  - sinon : la page `/tutor/abonnement` (état de l'essai, de l'abonnement,
 *    souscrire tout de suite, gérer).
 *
 * Achat : `PurchaseButton` (`jeraisonne_tutor_monthly`) → Stripe sur le web,
 * App Store / Google Play dans l'app (RevenueCat). L'accès arrive par webhook :
 * on interroge le serveur quelques secondes après l'achat ou le retour Stripe.
 */

import { useEffect, useState } from 'react';
import { markPaywallSeen } from '@/lib/analytics/track';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check, GraduationCap } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { PurchaseButton } from '@/components/purchase-button';
import { isNativeApp } from '@/lib/platform';
import {
  TUTOR_PRICE_LABEL,
  daysLabel,
  isStoreSubscription,
  shortDate,
  trialDaysLeft,
  tutorAccessQueryKey,
  type TutorAccess,
} from '@/lib/tutor/subscription';

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined) ?? 'https://jeraisonne.fr';

export const TUTOR_FEATURES: readonly string[] = [
  'Tes élèves et tes classes, leur progression en direct',
  'SKILLCARDS et exercices assignés : offerts à tes élèves',
  'Création d’exercices, de cartes et de générateurs avec l’IA',
  'Rendez-vous, cours en visio et messagerie',
  'Ton blog et ta fiche publique de prof',
];

/** Titre + phrase d'état. PUR, testé. */
export function tutorSubscriptionHeadline(
  access: TutorAccess,
  now: Date = new Date(),
): { title: string; detail: string } {
  const sub = access.subscription;
  switch (access.reason) {
    case 'admin':
      return { title: 'Compte admin', detail: 'L’espace prof est inclus, sans abonnement.' };
    case 'subscription': {
      const end = shortDate(sub?.currentPeriodEnd ?? null, now);
      return {
        title: 'Abonnement Prof actif',
        detail: end
          ? sub?.cancelAtPeriodEnd
            ? `Résilié : il prend fin le ${end}.`
            : `Prochain renouvellement le ${end}.`
          : 'Merci pour ton soutien !',
      };
    }
    case 'trial': {
      const left = trialDaysLeft(access.trialEndsAt, now);
      return {
        title: `Essai gratuit : encore ${daysLabel(left)}`,
        detail: `Tout l’espace prof est ouvert jusqu’au ${shortDate(access.trialEndsAt, now)}. Abonne-toi pour continuer ensuite.`,
      };
    }
    default:
      return sub
        ? {
            title: 'Ton abonnement Prof est terminé',
            detail: 'Abonne-toi de nouveau pour retrouver tes élèves, tes classes et tes contenus.',
          }
        : {
            title: `Ton essai gratuit de ${daysLabel(access.trialDays)} est terminé`,
            detail:
              'Abonne-toi pour continuer : tes élèves, tes classes et tes contenus t’attendent.',
          };
  }
}

/** Interroge l'accès toutes les 2,5 s (30 s max) après un achat. */
function usePollAfterPurchase(userId: string | null, done: boolean) {
  const qc = useQueryClient();
  const [polling, setPolling] = useState(false);

  // Retour du checkout Stripe : ?sub_success=1.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('sub_success') === '1') setPolling(true);
  }, []);

  useEffect(() => {
    if (!polling || done) return;
    let n = 0;
    const id = window.setInterval(() => {
      n += 1;
      qc.invalidateQueries({ queryKey: tutorAccessQueryKey(userId) });
      if (n >= 12) window.clearInterval(id);
    }, 2_500);
    return () => window.clearInterval(id);
  }, [polling, done, qc, userId]);

  return () => setPolling(true);
}

function ManageSubscription({ access }: { access: TutorAccess }) {
  const [loading, setLoading] = useState(false);
  const sub = access.subscription;
  if (!sub) return null;
  const store = isStoreSubscription(sub);
  const native = isNativeApp();

  async function openManage() {
    setLoading(true);
    try {
      if (store) {
        const { openManageSubscriptionsNative } = await import('@/lib/checkout/native');
        await openManageSubscriptionsNative();
        return;
      }
      const { data, error } = await supabase.functions.invoke('create-portal-session', {
        body: { return_to: window.location.href },
      });
      if (!error && data?.url) window.location.href = data.url as string;
    } finally {
      setLoading(false);
    }
  }

  // Abonnement pris ailleurs : on dit où le gérer, sans lien d'achat externe.
  if (store !== native) {
    return (
      <p className="text-ink-500 text-center font-sans text-xs leading-relaxed">
        {store
          ? 'Abonnement pris sur l’App Store ou Google Play : gère-le depuis ton téléphone.'
          : 'Abonnement pris sur le site jeraisonne.fr : gère-le depuis le site.'}
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={openManage}
      disabled={loading}
      className="border-ink-300 text-ink-700 min-h-11 w-full rounded-xl border py-2.5 font-sans text-sm font-semibold transition-all active:scale-[.98] disabled:opacity-60"
    >
      {loading ? 'Ouverture…' : 'Gérer mon abonnement'}
    </button>
  );
}

export function TutorSubscriptionPanel({
  access,
  userId,
  blocked = false,
}: {
  access: TutorAccess;
  userId: string | null;
  /** Essai ou abonnement terminé : le panneau remplace l'espace prof. */
  blocked?: boolean;
}) {
  const subscribed = access.reason === 'subscription';
  // Tunnel de conversion « contenu bloqué → achat » (0718) : essai terminé.
  useEffect(() => {
    if (blocked) markPaywallSeen('prof');
  }, [blocked]);
  const startPolling = usePollAfterPurchase(userId, subscribed);
  const { title, detail } = tutorSubscriptionHeadline(access);
  const canBuy = access.reason !== 'admin' && !subscribed;

  return (
    <section
      aria-labelledby="tutor-subscription-title"
      className={cn('mx-auto w-full max-w-xl space-y-5', blocked && 'py-8')}
    >
      <div className="space-y-2 text-center">
        <span className="bg-primary-50 text-primary-600 mx-auto flex h-12 w-12 items-center justify-center rounded-2xl">
          <GraduationCap className="h-6 w-6" aria-hidden />
        </span>
        <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
          Abonnement Prof
        </p>
        <h1
          id="tutor-subscription-title"
          className="font-display text-ink-900 text-2xl font-extrabold leading-tight"
        >
          {title}
        </h1>
        <p className="text-ink-600 font-sans text-sm leading-relaxed">{detail}</p>
      </div>

      {access.reason !== 'admin' && (
        <div className="border-primary-200 from-primary-50 to-ink-50 space-y-4 rounded-2xl border-2 bg-gradient-to-b p-5">
          <p className="text-center">
            <span className="font-display text-ink-900 text-3xl font-extrabold">
              {TUTOR_PRICE_LABEL.replace(' / mois', '')}
            </span>
            <span className="text-ink-500 font-sans text-sm"> / mois · sans engagement</span>
          </p>
          <ul className="space-y-2" aria-label="Inclus dans l’abonnement Prof">
            {TUTOR_FEATURES.map((f) => (
              <li key={f} className="text-ink-700 flex items-start gap-2 font-sans text-sm">
                <Check className="text-xp-600 mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                {f}
              </li>
            ))}
          </ul>
          {canBuy ? (
            <PurchaseButton
              productId="jeraisonne_tutor_monthly"
              showRestore
              onPurchased={startPolling}
            />
          ) : (
            <ManageSubscription access={access} />
          )}
          <p className="text-ink-400 text-center font-sans text-[11px] leading-relaxed">
            Renouvellement automatique chaque mois, résiliable à tout moment.{' '}
            <a href={`${MARKETING_URL}/cgu`} target="_blank" rel="noreferrer" className="underline">
              Conditions d’utilisation
            </a>{' '}
            ·{' '}
            <a
              href={`${MARKETING_URL}/confidentialite`}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Confidentialité
            </a>
          </p>
        </div>
      )}

      {blocked && (
        <p className="text-ink-500 text-center font-sans text-sm">
          <Link to="/profile" className="text-primary-600 font-semibold hover:underline">
            Mon profil et mes réglages
          </Link>
        </p>
      )}
    </section>
  );
}

/** Bandeau de l'essai en cours, en haut de l'espace prof. */
export function TutorTrialBanner({ access }: { access: TutorAccess }) {
  if (access.reason !== 'trial') return null;
  const left = trialDaysLeft(access.trialEndsAt);
  return (
    <div
      role="status"
      className="bg-primary-50 text-primary-800 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center font-sans text-xs font-semibold"
    >
      <span>Essai gratuit de l’espace prof : encore {daysLabel(left)}.</span>
      <Link to="/tutor/abonnement" className="text-primary-600 underline">
        S’abonner · {TUTOR_PRICE_LABEL}
      </Link>
    </div>
  );
}
